import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github, House } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithLogomarkSVG, BlacksmithWordmarkSVG } from "../BlacksmithLogoSVG";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useHasHoverPointer, useIsCompactViewport } from "@/hooks/use-device";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents, SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useAppRailHiddenOnMobile } from "./mobileRailVisibility";
import { useActiveApp, isHomePath, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_REDESIGN_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";
import { RAIL_SHORTCUT_LABELS, railShortcutSection, type RailShortcutSection } from "./railShortcuts";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (Home, GitHub Actions, Codesmith,
 * Proof), and the global footer controls (support, settings, notifications,
 * org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "home" | "actions" | "codesmith" | "proof" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;

// `timeStamp` of the last keydown into a text field (see railShortcutSection).
// Module scope, like pinnedExpanded, so it survives rail remounts.
let lastTextEntryAt = -Infinity;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(170);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : activeApp === "proof" ? "proof" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsCompactViewport();
  const hiddenOnMobile = useAppRailHiddenOnMobile(activeApp);
  const hasHoverPointer = useHasHoverPointer();
  const {
    activeGithubOrg,
    codesmithAutomationsEnabled,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== orgsResolved || $[2] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
    $[0] = activeGithubOrg;
    $[1] = orgsResolved;
    $[2] = urlOrg;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const currentOrg = t0;
  const homeHref = currentOrg ? `/${currentOrg}/home` : "/home";
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs";
  const sidebarVariant = useCodesmithSidebarVariant(currentOrg ?? null);
  const redesign = sidebarVariant === "redesign";
  const sessionsNewHref = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
  const proofHref = currentOrg ? `/${currentOrg}/proof` : "/proof";
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const clearOpenTimer = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removePortalTracker = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const installPortalTracker = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRailRowClick = t4;
  let t5;
  if ($[8] !== expanded || $[9] !== hasHoverPointer) {
    t5 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[8] = expanded;
    $[9] = hasHoverPointer;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t8 = [removePortalTracker, clearOpenTimer];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== router.events) {
    t9 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t10 = [router.events, removePortalTracker, clearOpenTimer];
    $[14] = router.events;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== router.events) {
    t11 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t12 = [router.events];
    $[17] = router.events;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== instantClose) {
    t13 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t14 = [instantClose];
    $[20] = instantClose;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[23] !== expanded) {
    t15 = () => {
      pinnedExpanded = expanded;
    };
    t16 = [expanded];
    $[23] = expanded;
    $[24] = t15;
    $[25] = t16;
  } else {
    t15 = $[24];
    t16 = $[25];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[26] !== layoutWidth) {
    t17 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t18 = [layoutWidth];
    $[26] = layoutWidth;
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  useLayoutEffect(_temp2, t19);
  let t20;
  let t21;
  if ($[30] !== codesmithEnabled || $[31] !== router || $[32] !== sessionsNewHref) {
    t20 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          setOptimisticSection("codesmith");
          router.push(sessionsNewHref);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t21 = [codesmithEnabled, sessionsNewHref, router];
    $[30] = codesmithEnabled;
    $[31] = router;
    $[32] = sessionsNewHref;
    $[33] = t20;
    $[34] = t21;
  } else {
    t20 = $[33];
    t21 = $[34];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[35] !== codesmithAutomationsEnabled || $[36] !== currentOrg || $[37] !== router) {
    t22 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          setOptimisticSection("codesmith");
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t23 = [codesmithAutomationsEnabled, currentOrg, router];
    $[35] = codesmithAutomationsEnabled;
    $[36] = currentOrg;
    $[37] = router;
    $[38] = t22;
    $[39] = t23;
  } else {
    t22 = $[38];
    t23 = $[39];
  }
  useEffect(t22, t23);
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [];
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  useEffect(_temp4, t24);
  const railShortcutsActive = routeSection !== "codesmith" && optimisticSection !== "codesmith";
  let t25;
  let t26;
  if ($[41] !== codesmithEnabled || $[42] !== gPrefixActiveRef || $[43] !== homeHref || $[44] !== proofHref || $[45] !== railShortcutsActive || $[46] !== router || $[47] !== runsJobsHref || $[48] !== sessionsNewHref) {
    t25 = () => {
      if (!railShortcutsActive) {
        return;
      }
      const hrefs = {
        home: homeHref,
        actions: runsJobsHref,
        codesmith: codesmithEnabled ? sessionsNewHref : null,
        proof: codesmithEnabled ? proofHref : null
      };
      const handleRailShortcut = e_4 => {
        if (gPrefixActiveRef.current) {
          return;
        }
        const section_0 = railShortcutSection(e_4, lastTextEntryAt);
        const href = section_0 && hrefs[section_0];
        if (!section_0 || !href) {
          return;
        }
        e_4.preventDefault();
        setOptimisticSection(section_0);
        router.push(href);
      };
      document.addEventListener("keydown", handleRailShortcut);
      return () => document.removeEventListener("keydown", handleRailShortcut);
    };
    t26 = [railShortcutsActive, homeHref, runsJobsHref, codesmithEnabled, sessionsNewHref, proofHref, gPrefixActiveRef, router];
    $[41] = codesmithEnabled;
    $[42] = gPrefixActiveRef;
    $[43] = homeHref;
    $[44] = proofHref;
    $[45] = railShortcutsActive;
    $[46] = router;
    $[47] = runsJobsHref;
    $[48] = sessionsNewHref;
    $[49] = t25;
    $[50] = t26;
  } else {
    t25 = $[49];
    t26 = $[50];
  }
  useEffect(t25, t26);
  const t27 = currentOrg ?? undefined;
  let t28;
  if ($[51] !== globalOrgBlocked || $[52] !== t27) {
    t28 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} currentOrg={t27} />;
    $[51] = globalOrgBlocked;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  const t29 = expanded ? "expanded" : "collapsed";
  const t30 = redesign && !expanded && RAIL_REDESIGN_CLASS;
  const t31 = hiddenOnMobile && "max-md:hidden";
  let t32;
  if ($[54] !== t30 || $[55] !== t31) {
    t32 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t30, t31);
    $[54] = t30;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  const t33 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t34;
  if ($[57] !== layoutWidth || $[58] !== t33) {
    t34 = {
      width: layoutWidth,
      transition: t33
    };
    $[57] = layoutWidth;
    $[58] = t33;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  const t35 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t36;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setExpanded(_temp5);
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  const t37 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t38 = expanded ? "opacity-0" : "opacity-100";
  const t39 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t40;
  if ($[61] !== t38 || $[62] !== t39) {
    t40 = cn("flex size-8 shrink-0 items-center justify-center", t38, t39);
    $[61] = t38;
    $[62] = t39;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  let t42;
  if ($[65] !== t40) {
    t42 = <div className={t40}>{t41}</div>;
    $[65] = t40;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] !== expanded || $[68] !== instantClose) {
    t43 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[67] = expanded;
    $[68] = instantClose;
    $[69] = t43;
  } else {
    t43 = $[69];
  }
  let t44;
  if ($[70] !== t37 || $[71] !== t42 || $[72] !== t43) {
    t44 = <button type="button" onClick={t36} aria-label={t37} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t42}{t43}</button>;
    $[70] = t37;
    $[71] = t42;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== t35 || $[75] !== t44) {
    t45 = <AppHeaderBar><CollapsedIconTooltip label={t35} enabled={true}>{t44}</CollapsedIconTooltip></AppHeaderBar>;
    $[74] = t35;
    $[75] = t44;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  const t46 = redesign ? undefined : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t47;
  if ($[77] !== expanded) {
    t47 = <GitHubOrgSelector expanded={expanded} />;
    $[77] = expanded;
    $[78] = t47;
  } else {
    t47 = $[78];
  }
  let t48;
  if ($[79] !== t46 || $[80] !== t47) {
    t48 = <div className={t46}>{t47}</div>;
    $[79] = t46;
    $[80] = t47;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  const t49 = !redesign && "gap-1 pt-1";
  let t50;
  if ($[82] !== t49) {
    t50 = cn("rail-rows flex flex-col", t49);
    $[82] = t49;
    $[83] = t50;
  } else {
    t50 = $[83];
  }
  const t51 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.home : undefined;
  const t52 = currentOrg ? true : null;
  const t53 = !!currentOrg;
  let t54;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = handleRailRowClick("home");
    $[84] = t54;
  } else {
    t54 = $[84];
  }
  const t55 = activeSection === "home" ? "page" : undefined;
  const t56 = activeSection === "home" && RAIL_ROW_ACTIVE;
  let t57;
  if ($[85] !== t56) {
    t57 = cn(RAIL_ROW, t56);
    $[85] = t56;
    $[86] = t57;
  } else {
    t57 = $[86];
  }
  let t58;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <RailRowContents icon={<House className="size-[18px]" />} label="Home" />;
    $[87] = t58;
  } else {
    t58 = $[87];
  }
  let t59;
  if ($[88] !== homeHref || $[89] !== t52 || $[90] !== t53 || $[91] !== t55 || $[92] !== t57) {
    t59 = <RoutePrefetchLink href={homeHref} prefetch={t52} prefetchOnIntent={t53} onClick={t54} aria-current={t55} className={t57}>{t58}</RoutePrefetchLink>;
    $[88] = homeHref;
    $[89] = t52;
    $[90] = t53;
    $[91] = t55;
    $[92] = t57;
    $[93] = t59;
  } else {
    t59 = $[93];
  }
  let t60;
  if ($[94] !== slim || $[95] !== t51 || $[96] !== t59) {
    t60 = <CollapsedIconTooltip label="Home" enabled={slim} shortcut={t51}>{t59}</CollapsedIconTooltip>;
    $[94] = slim;
    $[95] = t51;
    $[96] = t59;
    $[97] = t60;
  } else {
    t60 = $[97];
  }
  const t61 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.actions : undefined;
  const t62 = currentOrg ? true : null;
  const t63 = !!currentOrg;
  let t64;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = handleRailRowClick("actions");
    $[98] = t64;
  } else {
    t64 = $[98];
  }
  const t65 = activeSection === "actions" ? "page" : undefined;
  const t66 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t67;
  if ($[99] !== t66) {
    t67 = cn(RAIL_ROW, t66);
    $[99] = t66;
    $[100] = t67;
  } else {
    t67 = $[100];
  }
  let t68;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[101] = t68;
  } else {
    t68 = $[101];
  }
  let t69;
  if ($[102] !== runsJobsHref || $[103] !== t62 || $[104] !== t63 || $[105] !== t65 || $[106] !== t67) {
    t69 = <RoutePrefetchLink href={runsJobsHref} prefetch={t62} prefetchOnIntent={t63} onClick={t64} aria-current={t65} className={t67}>{t68}</RoutePrefetchLink>;
    $[102] = runsJobsHref;
    $[103] = t62;
    $[104] = t63;
    $[105] = t65;
    $[106] = t67;
    $[107] = t69;
  } else {
    t69 = $[107];
  }
  let t70;
  if ($[108] !== slim || $[109] !== t61 || $[110] !== t69) {
    t70 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim} shortcut={t61}>{t69}</CollapsedIconTooltip>;
    $[108] = slim;
    $[109] = t61;
    $[110] = t69;
    $[111] = t70;
  } else {
    t70 = $[111];
  }
  let t71;
  if ($[112] !== activeSection || $[113] !== codesmithEnabled || $[114] !== codesmithRowHovered || $[115] !== currentOrg || $[116] !== railShortcutsActive || $[117] !== sessionsNewHref || $[118] !== slim) {
    t71 = codesmithEnabled && (() => {
      const codesmithRow = <RoutePrefetchLink href={sessionsNewHref} prefetchOnIntent={!!currentOrg} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></RoutePrefetchLink>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span>{railShortcutsActive && <ShortcutKeys keys={[RAIL_SHORTCUT_LABELS.codesmith]} />}</TooltipContent>}</Tooltip>;
    })();
    $[112] = activeSection;
    $[113] = codesmithEnabled;
    $[114] = codesmithRowHovered;
    $[115] = currentOrg;
    $[116] = railShortcutsActive;
    $[117] = sessionsNewHref;
    $[118] = slim;
    $[119] = t71;
  } else {
    t71 = $[119];
  }
  let t72;
  if ($[120] !== activeSection || $[121] !== codesmithEnabled || $[122] !== currentOrg || $[123] !== proofHref || $[124] !== railShortcutsActive || $[125] !== slim) {
    t72 = codesmithEnabled && <CollapsedIconTooltip label="Proof" enabled={slim} shortcut={railShortcutsActive ? RAIL_SHORTCUT_LABELS.proof : undefined}><RoutePrefetchLink href={proofHref} prefetchOnIntent={!!currentOrg} onClick={handleRailRowClick("proof")} aria-current={activeSection === "proof" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "proof" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<ProofIconBold className="size-[18px]" />} label="Proof" /></RoutePrefetchLink></CollapsedIconTooltip>;
    $[120] = activeSection;
    $[121] = codesmithEnabled;
    $[122] = currentOrg;
    $[123] = proofHref;
    $[124] = railShortcutsActive;
    $[125] = slim;
    $[126] = t72;
  } else {
    t72 = $[126];
  }
  let t73;
  if ($[127] !== t50 || $[128] !== t60 || $[129] !== t70 || $[130] !== t71 || $[131] !== t72) {
    t73 = <nav className={t50} aria-label="Apps">{t60}{t70}{t71}{t72}</nav>;
    $[127] = t50;
    $[128] = t60;
    $[129] = t70;
    $[130] = t71;
    $[131] = t72;
    $[132] = t73;
  } else {
    t73 = $[132];
  }
  let t74;
  if ($[133] !== activeSection || $[134] !== expanded || $[135] !== redesign || $[136] !== slim) {
    t74 = !redesign && <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} />;
    $[133] = activeSection;
    $[134] = expanded;
    $[135] = redesign;
    $[136] = slim;
    $[137] = t74;
  } else {
    t74 = $[137];
  }
  let t75;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <div className="flex-1" />;
    $[138] = t75;
  } else {
    t75 = $[138];
  }
  let t76;
  if ($[139] !== activeSection || $[140] !== expanded || $[141] !== redesign || $[142] !== slim) {
    t76 = redesign ? <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} divider={false} /> : null;
    $[139] = activeSection;
    $[140] = expanded;
    $[141] = redesign;
    $[142] = slim;
    $[143] = t76;
  } else {
    t76 = $[143];
  }
  const t77 = currentOrg ?? undefined;
  const t78 = activeApp === "codesmith";
  let t79;
  if ($[144] !== currentOrg || $[145] !== t78) {
    t79 = settingsPagePath(currentOrg, t78);
    $[144] = currentOrg;
    $[145] = t78;
    $[146] = t79;
  } else {
    t79 = $[146];
  }
  let t80;
  if ($[147] !== activeSection || $[148] !== expanded || $[149] !== redesign || $[150] !== slim || $[151] !== t76 || $[152] !== t77 || $[153] !== t79) {
    t80 = <SidebarGlobalFooter leading={t76} expanded={expanded} currentOrg={t77} settingsHref={t79} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} redesign={redesign} />;
    $[147] = activeSection;
    $[148] = expanded;
    $[149] = redesign;
    $[150] = slim;
    $[151] = t76;
    $[152] = t77;
    $[153] = t79;
    $[154] = t80;
  } else {
    t80 = $[154];
  }
  let t81;
  if ($[155] !== t45 || $[156] !== t48 || $[157] !== t73 || $[158] !== t74 || $[159] !== t80) {
    t81 = <div className="flex h-full w-full flex-col px-2">{t45}{t48}{t73}{t74}{t75}{t80}</div>;
    $[155] = t45;
    $[156] = t48;
    $[157] = t73;
    $[158] = t74;
    $[159] = t80;
    $[160] = t81;
  } else {
    t81 = $[160];
  }
  let t82;
  if ($[161] !== handleMouseEnter || $[162] !== t29 || $[163] !== t32 || $[164] !== t34 || $[165] !== t81) {
    t82 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t29} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t32} style={t34}>{t81}</div>;
    $[161] = handleMouseEnter;
    $[162] = t29;
    $[163] = t32;
    $[164] = t34;
    $[165] = t81;
    $[166] = t82;
  } else {
    t82 = $[166];
  }
  let t83;
  if ($[167] !== t28 || $[168] !== t82) {
    t83 = <>{t28}{t82}</>;
    $[167] = t28;
    $[168] = t82;
    $[169] = t83;
  } else {
    t83 = $[169];
  }
  return t83;
}
function _temp5(prev) {
  return !prev;
}
function _temp4() {
  const noteTextEntry = _temp3;
  document.addEventListener("keydown", noteTextEntry, true);
  return () => document.removeEventListener("keydown", noteTextEntry, true);
}
function _temp3(e_3) {
  if (isTypingTarget(e_3.target)) {
    lastTextEntryAt = e_3.timeStamp;
  }
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}